import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import ObrasApi from 'Data/Obras.api';
import { FILES_QUERY_KEY } from 'Data/keys/QueryKeys';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { useDispatch } from 'react-redux';
import { ViewerActions } from 'Store/Viewer/Viewer.actions';
import { IVerifyAutodeskProgress } from 'Data/interfaces/ListagemArquivos/IVerifyAutodeskProgress';
import { BucketFileStatusEnum, IFileData, IRenderingSignalR } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { IAutodeskViewer } from 'Data/interfaces/Viewer/IAutodeskViewer';
import { isNativeUrn, withConversionStatus } from 'Data/interfaces/Viewer/INativeViewerArtifacts';
import { ViewerEngineEnum } from 'Data/enums/ViewerEngine';

export const useQueryVerifyAutodeskProfressFiles = () => {
  const dispatch = useDispatch();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: (variables: IVerifyAutodeskProgress) => {
      return ObrasApi.verifyFileRenderProgress(
        variables.fileApiId,
        variables.constructionSiteId,
      );
    },
    onSuccess: async ({ data }, variables) => {
      await queryClient.cancelQueries({
        queryKey: [FILES_QUERY_KEY, Number(variables.constructionSiteId), variables.folderId],
      });

      let etapa = 0;
      let progress =
        data.data.progress && data.data.progress.indexOf('%') !== -1
          ? Number(data.data.progress.split('%')[0])
          : 0;

      switch (data.data.status) {
        case 'inprogress':
          etapa = 3;
          break;
        case 'success':
          etapa = 4;
          progress = 100;
          break;
        case 'notstarted':
          etapa = 1;
          break;
        case 'failed':
          etapa = 5;
          progress = 100;
          break;
      }

      const body: IRenderingSignalR = {
        etapa,
        fileIdentifier: variables.fileApiId || '',
        progress,
        urn: data.data.urn,
      };

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.constructionSiteId), variables.folderId],
        (old) => {
          if (old) {
            const index = old.ListFiles.findIndex((file) => {
              return file.Identifier.ApiId === body.fileIdentifier;
            });
            if (index !== -1) {
              if (!old.ListFiles[index].RenderingSignalR) {
                old.ListFiles[index].RenderingSignalR = {
                  ...body,
                  etapa: 1,
                  progress: 0,
                };
              }
              if (
                body.etapa > old.ListFiles[index].RenderingSignalR.etapa ||
                (body.etapa === old.ListFiles[index].RenderingSignalR.etapa &&
                  body.progress > old.ListFiles[index].RenderingSignalR.progress)
              ) {
                old.ListFiles[index].RenderingSignalR = { ...body };
                old.ListFiles[index].RenderingStatus = 2;
              }
              if (body.etapa === 4) {
                old.ListFiles[index].RenderingSignalR = {
                  ...old.ListFiles[index].RenderingSignalR,
                  etapa: 0,
                  progress: 0,
                };
                old.ListFiles[index].RenderingStatus = 1;
              }
              if (body.etapa === 5) {
                old.ListFiles[index].RenderingSignalR = {
                  ...body,
                  progress: 0,
                };
                old.ListFiles[index].RenderingStatus = 3;
              }
            }
            return { ...old };
          }
          return old;
        },
      );
      dispatch(ViewerActions.updateStatusFileViewerAutodesk(body));
    },
    // A consulta falha justamente enquanto o processamento não terminou (a
    // Autodesk ainda não tem o manifest do arquivo). Para o usuário isso não é
    // erro: a tela segue mostrando a etapa, e o hub traz o andamento real.
    onError: (err, variables) => {
      console.warn('[viewer] progresso da renderização indisponível; seguindo pelo hub.', {
        fileApiId: variables.fileApiId,
        err,
      });
    },
  });

  return {
    verifyAutodeskProfressFiles: mutate,
    verifyAutodeskProfressFilesAsync: mutateAsync,
    isLoading: isPending,
  };
};

/**
 * Aplica um evento do hub na linha do arquivo, em QUALQUER cache de listagem que
 * a contenha.
 *
 * Não usa o `folderId` para achar a entrada: o `ViewerSignalR` é `memo(() =>
 * true)`, nunca re-renderiza, e por isso os handlers ficam com o `folderId` da
 * primeira pasta aberta na sessão. Escrever naquela chave deixava o progresso
 * preso em "Renderizando" em toda pasta visitada depois. O `constructionSiteId`
 * segue valendo — ele não muda sem remontar a página.
 */
// Com `engine` no evento, o status vai para a conversão daquele motor e a linha
// recalcula a mostrada; sem ele (backend anterior), vale a única conversão.
const withEventStatus = (file: IFileData, data: IRenderingSignalR, status: BucketFileStatusEnum): IFileData => (
  data.engine ? withConversionStatus(file, data.engine, status) : { ...file, RenderingStatus: status }
);

export const setFileStatusAutodeskRendering = async (
  constructionSiteId: number,
  _folderId: string,
  data: IRenderingSignalR,
) => {
  const caches = queryClient.getQueryCache()
    .findAll({ queryKey: [FILES_QUERY_KEY, Number(constructionSiteId)] })
    .filter((query) => (query.state.data as IUseQueryFilesReturn | undefined)?.ListFiles
      ?.some((file) => file.Identifier.ApiId === data.fileIdentifier));

  if (caches.length === 0) { return; }

  await Promise.all(caches.map((query) => queryClient.cancelQueries({ queryKey: query.queryKey })));

  for (const query of caches) {
    queryClient.setQueryData<IUseQueryFilesReturn>(query.queryKey, (old) => {
      if (!old) { return old; }

      return {
        ...old,
        ListFiles: old.ListFiles.map((file) => {
          if (file.Identifier.ApiId !== data.fileIdentifier) { return file; }

          // Término ANTES do avanço: o evento final (etapa 4 ou 5) também é
          // uma etapa maior que a atual, e checado depois do avanço nunca era
          // alcançado — a linha ficava em "Renderizando" até o refresh.
          if (data.etapa === 4) {
            if (data.engine) {
              return {
                ...withEventStatus(file, data, BucketFileStatusEnum.Rendered),
                RenderingSignalR: { ...data, etapa: 0, progress: 0 },
                Urn: data.urn ?? file.Urn,
              };
            }
            // Backend anterior: a urn do evento é o que marca a linha como nativa;
            // sem ela o clique pediria o render de novo em vez de abrir.
            return {
              ...file,
              RenderingSignalR: { ...data, etapa: 0, progress: 0 },
              RenderingStatus: 1,
              Urn: data.urn ?? file.Urn,
              RenderEngine: isNativeUrn(data.urn) ? ViewerEngineEnum.Native : file.RenderEngine,
            };
          }
          if (data.etapa === 5) {
            return {
              ...withEventStatus(file, data, BucketFileStatusEnum.Error),
              RenderingSignalR: { ...data, progress: 0 },
            };
          }
          if (!file.RenderingSignalR) {
            return {
              ...file,
              RenderingSignalR: { ...data, etapa: 1, progress: 0 },
            };
          }
          if (
            data.etapa > file.RenderingSignalR.etapa ||
            (data.etapa === file.RenderingSignalR.etapa &&
              data.progress > file.RenderingSignalR.progress)
          ) {
            return {
              ...withEventStatus(file, data, BucketFileStatusEnum.Rendering),
              RenderingSignalR: { ...data },
            };
          }

          return file;
        }),
      };
    });
  }
};

/**
 * Desfaz o "renderizando" otimista de um pedido que não aconteceu (sem créditos,
 * recusado). Só zerar a etapa deixava o status em "Renderizando" até o refresh.
 * `previousStatus` = o status de antes do clique (erro, num re-render).
 */
export const resetFileStatusRendering = async (
  data: IAutodeskViewer,
  previousStatus: BucketFileStatusEnum = BucketFileStatusEnum.NotRendered,
) => {
  await queryClient.cancelQueries({
    queryKey: [FILES_QUERY_KEY, Number(data.constructionSiteId), data.folderId],
  });

  queryClient.setQueryData<IUseQueryFilesReturn>(
    [FILES_QUERY_KEY, Number(data.constructionSiteId), data.folderId],
    (old) => {
      if (old) {
        return {
          ...old,
          ListFiles: old.ListFiles.map((file) => {
            if (file.Identifier.ApiId === data.fileApiId) {
              return {
                ...file,
                RenderingStatus: previousStatus,
                RenderingSignalR: {
                  etapa: 0,
                  progress: 0,
                  fileIdentifier: data.fileApiId,
                },
              };
            }
            return file;
          }),
        };
      }
      return old;
    },
  );
};
